import { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/feature/ScrollToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import { categories, movies } from "@/mocks/data";

export default function Category() {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentType = searchParams.get("type") || "all";
  const [sortBy, setSortBy] = useState("default");

  const currentCategory = categories.find((c) => c.id === currentType);

  useEffect(() => {
    const title =
      currentType === "all"
        ? "全部分类 - 大师兄影视"
        : `${currentCategory?.name || ""} - 大师兄影视`;
    document.title = title;
  }, [currentType, currentCategory]);

  const filteredMovies = useMemo(() => {
    let result =
      currentType === "all"
        ? [...movies]
        : movies.filter((m) => m.category === currentType);

    switch (sortBy) {
      case "rating":
        result.sort((a, b) => b.rating - a.rating);
        break;
      case "newest":
        result.sort((a, b) => b.year - a.year);
        break;
      default:
        break;
    }
    return result;
  }, [currentType, sortBy]);

  const handleCategoryChange = (typeId: string) => {
    if (typeId === "all") {
      setSearchParams({});
    } else {
      setSearchParams({ type: typeId });
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-background-50">
      <ScrollToTop />
      <Navbar />

      <main className="pt-20 pb-12">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6">
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: "首页", path: "/" },
                { label: currentCategory?.name || "全部分类" },
              ]}
            />
          </div>

          <div className="mb-8">
            <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-1">
              {currentCategory?.name || "全部分类"}
            </h1>
            <p className="text-sm text-foreground-500">
              共 {filteredMovies.length} 部作品
            </p>
          </div>

          <div className="flex flex-col md:flex-row gap-6">
            <aside className="w-full md:w-40 shrink-0">
              <div className="flex md:flex-col gap-1.5 overflow-x-auto md:overflow-visible pb-2 md:pb-0">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => handleCategoryChange(cat.id)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      currentType === cat.id
                        ? "bg-primary-500 text-white"
                        : "bg-background-100 text-foreground-600 hover:bg-background-200"
                    }`}
                  >
                    <span className="w-5 h-5 flex items-center justify-center">
                      <i className={`${cat.icon} text-sm`}></i>
                    </span>
                    {cat.name}
                  </button>
                ))}
              </div>
            </aside>

            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-5">
                <span className="text-xs text-foreground-500 whitespace-nowrap">
                  找到 {filteredMovies.length} 部作品
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-foreground-500 whitespace-nowrap">排序：</span>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="px-3 py-1.5 rounded-lg text-xs bg-background-100 text-foreground-700 border border-background-200 cursor-pointer outline-none focus:border-primary-400"
                  >
                    <option value="default">默认排序</option>
                    <option value="rating">评分最高</option>
                    <option value="newest">最新上映</option>
                  </select>
                </div>
              </div>

              {filteredMovies.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4">
                  {filteredMovies.map((movie) => (
                    <MovieCard
                      key={movie.id}
                      id={movie.id}
                      title={movie.title}
                      genre={movie.genre}
                      rating={movie.rating}
                      poster={movie.poster}
                      updateInfo={movie.updateInfo}
                      isNew={movie.isNew}
                      isHot={movie.isHot}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
                    <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-sm text-foreground-500">该分类暂无内容</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": `${currentCategory?.name || "全部分类"} - 大师兄影视`,
            "url": `https://www.dashixiong.tv/category${currentType !== "all" ? `?type=${currentType}` : ""}`,
            "isPartOf": {
              "@type": "WebSite",
              "name": "大师兄影视",
              "url": "https://www.dashixiong.tv/",
            },
            "breadcrumb": {
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "首页",
                  "item": "https://www.dashixiong.tv/",
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "name": currentCategory?.name || "全部分类",
                },
              ],
            },
          }),
        }}
      />
    </div>
  );
}